/** Due dates are whole days, written YYYY-MM-DD in the lab's own calendar. */

export type DueBucket = 'overdue' | 'today' | 'upcoming';

const DAY = /^\d{4}-\d{2}-\d{2}$/;

export const isDay = (s: string | null | undefined): s is string =>
  typeof s === 'string' && DAY.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`));

/** A date as YYYY-MM-DD in the person's own time zone. */
export function localDay(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** The day `n` days after `day`, without trouble from clocks changing. */
export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function dueBucket(due: string, today: string): DueBucket {
  return due < today ? 'overdue' : due === today ? 'today' : 'upcoming';
}

/** When a report made today should be ready: the slowest test sets the day. */
export function defaultDueDate(
  docs: readonly { turnaroundDays?: number | undefined }[],
  today: string = localDay(),
): string {
  const days = Math.max(0, ...docs.map((d) => d.turnaroundDays ?? 0));
  return addDays(today, days);
}

/** "today", "tomorrow", "yesterday" or "12 Oct", for lists and reminders. */
export function describeDue(due: string, today: string = localDay()): string {
  const diff = Math.round(
    (Date.parse(`${due}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000,
  );
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff === -1) return 'yesterday';
  const d = new Date(`${due}T00:00:00Z`);
  const label = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return diff < 0 ? `${-diff} days ago (${label})` : label;
}
